import React, { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { FilterChip } from "./FilterChip";
import { SingleSelectFilter } from "./SingleSelectFilter";
import { PlatformFilter } from "./PlatformFilter";
import { TierFilter } from "./TierFilter";
import { EngagementFilter } from "./EngagementFilter";
import {
  LOCATIONS,
  CATEGORIES,
  LANGUAGES,
  GENDERS,
  TIERS,
  ENGAGEMENT_RANGES,
} from "./constants";

export interface DiscoveryFiltersProps {
  search: string;
  setSearch: (v: string) => void;
  selectedQuick: string;
  setSelectedQuick: (v: string) => void;
  selectedPlatforms: string[];
  setSelectedPlatforms: React.Dispatch<React.SetStateAction<string[]>>;
  location: string;
  setLocation: (v: string) => void;
  category: string;
  setCategory: (v: string) => void;
  language: string;
  setLanguage: (v: string) => void;
  minFollowers: number;
  setMinFollowers: (v: number) => void;
  maxFollowers: number;
  setMaxFollowers: (v: number) => void;
  selectedTier: string;
  setSelectedTier: (v: string) => void;
  minEngagement: number;
  setMinEngagement: (v: number) => void;
  maxEngagement: number;
  setMaxEngagement: (v: number) => void;
  selectedEngagementRanges: string[];
  setSelectedEngagementRanges: React.Dispatch<React.SetStateAction<string[]>>;
  gender: string;
  setGender: (v: string) => void;
  onClear?: () => void;
}

export const DiscoveryFilters: React.FC<DiscoveryFiltersProps> = ({
  search,
  setSearch,
  selectedPlatforms,
  setSelectedPlatforms,
  location,
  setLocation,
  category,
  setCategory,
  language,
  setLanguage,
  setMinFollowers,
  setMaxFollowers,
  selectedTier,
  setSelectedTier,
  minEngagement,
  setMinEngagement,
  maxEngagement,
  setMaxEngagement,
  gender,
  setGender,
  selectedEngagementRanges,
  setSelectedEngagementRanges,
  onClear,
}) => {
  const handleTierSelect = useCallback(
    (tierValue: string) => {
      if (selectedTier === tierValue) {
        setSelectedTier("");
        setMinFollowers(0);
        setMaxFollowers(0);
      } else {
        setSelectedTier(tierValue);
        const tier = TIERS.find((t) => t.value === tierValue);
        if (tier) {
          setMinFollowers(tier.min);
          setMaxFollowers(tier.max);
        }
      }
    },
    [selectedTier, setSelectedTier, setMinFollowers, setMaxFollowers]
  );

  const handleClearTier = useCallback(() => {
    setSelectedTier("");
    setMinFollowers(0);
    setMaxFollowers(0);
  }, [setSelectedTier, setMinFollowers, setMaxFollowers]);

  const handleEngagementRangeSelect = useCallback(
    (rangeValue: string) => {
      setSelectedEngagementRanges((prev) => {
        const newRanges = prev.includes(rangeValue)
          ? prev.filter((r) => r !== rangeValue)
          : [...prev, rangeValue];

        if (newRanges.length === 0) {
          setMinEngagement(0);
          setMaxEngagement(0);
        } else {
          const selected = ENGAGEMENT_RANGES.filter((r) => newRanges.includes(r.value));
          setMinEngagement(Math.min(...selected.map((r) => r.min)));
          setMaxEngagement(Math.max(...selected.map((r) => r.max)));
        }

        return newRanges;
      });
    },
    [setSelectedEngagementRanges, setMinEngagement, setMaxEngagement]
  );

  const handleClearEngagement = useCallback(() => {
    setSelectedEngagementRanges([]);
    setMinEngagement(0);
    setMaxEngagement(0);
  }, [setSelectedEngagementRanges, setMinEngagement, setMaxEngagement]);

  return (
    <div className="p-4 mb-4">
      {/* Search bar */}
      <div className="mb-6 flex items-center gap-[30px]">
        <input
          type="text"
          placeholder="Search creators by name, keyword, niche…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 h-[60px] rounded-full bg-[#FAFAFA] px-6 pr-16 text-base placeholder:text-gray-400 focus:ring-2 focus:outline-none transition focus:ring-[#9333EA]"
        />
        <button
          type="button"
          className="h-[60px] w-[60px] rounded-full bg-[#1E1C26] flex items-center justify-center hover:opacity-90 transition shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </button>
      </div>

      <div className="w-full h-px bg-[#9A9A9A] my-6" />

      {/* Filter dropdowns */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <PlatformFilter
          selectedPlatforms={selectedPlatforms}
          setSelectedPlatforms={setSelectedPlatforms}
        />
        <TierFilter
          selectedTier={selectedTier}
          onTierSelect={handleTierSelect}
          onClearTier={handleClearTier}
        />
        <EngagementFilter
          selectedEngagementRanges={selectedEngagementRanges}
          onRangeSelect={handleEngagementRangeSelect}
          onClearEngagement={handleClearEngagement}
        />
        <SingleSelectFilter
          title="Location"
          value={location}
          setValue={setLocation}
          options={LOCATIONS.map((x) => ({ label: x, value: x }))}
          width="w-[220px]"
        />
        <SingleSelectFilter
          title="Category"
          value={category}
          setValue={setCategory}
          options={CATEGORIES.map((x) => ({ label: x, value: x }))}
          width="w-[240px]"
        />
        <SingleSelectFilter
          title="Language"
          value={language}
          setValue={setLanguage}
          options={LANGUAGES.map((x) => ({ label: x, value: x }))}
          width="w-[220px]"
        />
        <SingleSelectFilter
          title="Gender"
          value={gender}
          setValue={setGender}
          options={GENDERS}
          width="w-[200px]"
        />
      </div>

      {/* Active filter chips */}
      <div className="flex flex-wrap gap-3 mb-4">
        {selectedPlatforms.includes("instagram") && (
          <FilterChip
            label="Instagram"
            onRemove={() => setSelectedPlatforms((prev) => prev.filter((p) => p !== "instagram"))}
          />
        )}
        {selectedPlatforms.includes("youtube") && (
          <FilterChip
            label="YouTube"
            onRemove={() => setSelectedPlatforms((prev) => prev.filter((p) => p !== "youtube"))}
          />
        )}
        {selectedTier && (
          <FilterChip label={selectedTier} onRemove={() => handleTierSelect(selectedTier)} />
        )}
        {selectedEngagementRanges.map((r) => {
          const range = ENGAGEMENT_RANGES.find((x) => x.value === r);
          return (
            <FilterChip
              key={r}
              label={range?.label || r}
              onRemove={() => handleEngagementRangeSelect(r)}
            />
          );
        })}
        {location && <FilterChip label={location} onRemove={() => setLocation("")} />}
        {category && <FilterChip label={category} onRemove={() => setCategory("")} />}
        {language && <FilterChip label={language} onRemove={() => setLanguage("")} />}
        {gender && <FilterChip label={gender} onRemove={() => setGender("")} />}
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 mt-2">
        <Button variant="outline" size="sm" type="button" onClick={onClear}>
          Clear All
        </Button>
        <Button variant="default" size="sm" type="button">
          Save Segment
        </Button>
      </div>
    </div>
  );
};
